import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import useQuery from "../search/useQuery";
import QUERY_TAGS from "../search/queryTags";
import { fetchPayouts } from "./financeService";
import { EMPTY_PAGE, normalizeSpringPage } from "./financePage";
import { mapPayouts } from "./financeMappers";
import { VOLATILE_STALE_TIME } from "./financeCache";

export const PAYOUT_PAGE_STEP = 20;
export const PAYOUT_SORT = "createdAt,desc";

const usePayouts = ({ status, pageSize = PAYOUT_PAGE_STEP, enabled = true } = {}) => {
  const [size, setSize] = useState(pageSize);
  const settledRef = useRef(EMPTY_PAGE);

  useEffect(() => {
    setSize(pageSize);
    settledRef.current = EMPTY_PAGE;
  }, [status, pageSize]);

  const query = useQuery({
    key: enabled ? `finance:payouts:${status || "all"}:${size}` : null,
    tag: QUERY_TAGS.PAYOUT_LIST,
    staleTime: VOLATILE_STALE_TIME,
    enabled,
    fetcher: () => fetchPayouts({ page: 0, size, sort: PAYOUT_SORT, status }),
  });

  const fresh = useMemo(() => normalizeSpringPage(query.currentData), [query.currentData]);
  const hasFresh = query.currentData !== undefined;
  const page = hasFresh ? fresh : settledRef.current;

  useEffect(() => {
    if (hasFresh) settledRef.current = fresh;
  }, [hasFresh, fresh]);

  const items = useMemo(() => mapPayouts(page.items), [page]);

  const loadMore = useCallback(() => {
    setSize((current) => current + PAYOUT_PAGE_STEP);
  }, []);

  const hasData = hasFresh || settledRef.current !== EMPTY_PAGE;

  return {
    items,
    total: page.total,
    isLoading: !hasData && !query.isError && query.enabled,
    isFetching: query.isFetching,
    isError: query.isError && !hasData,
    error: query.error,
    canLoadMore: !page.isLast && !query.isFetching && !query.isError,
    loadMore,
    refetch: query.refetch,
  };
};

export default usePayouts;
